import React, { forwardRef } from "react";
import { cn } from "@/lib/utils";

const VARIANT = {
  default:
    "bg-white text-frac-black border border-frac-gray-10 shadow-elev-sm",
  outlined: "bg-transparent text-frac-black border border-frac-gray-30",
  dark: "bg-frac-black text-frac-white border border-hairline",
  gradient: "bg-gradient-brand text-frac-white",
  glass:
    "bg-[rgba(21,10,36,0.6)] text-ink-primary border border-hairline backdrop-blur-[20px]",
};

/**
 * Card primitive — padding 24px, radius 16px. Set `interactive` to enable
 * a lift on hover (glass variant especially). All other transitions cost
 * exactly 200ms with the site ease.
 */
export const Card = forwardRef(function Card(
  {
    variant = "glass",
    interactive = false,
    className,
    children,
    as: As = "div",
    ...rest
  },
  ref,
) {
  return (
    <As
      ref={ref}
      className={cn(
        "rounded-[16px] p-6 transition-all duration-base ease-out",
        VARIANT[variant],
        interactive &&
          "hover:-translate-y-1 hover:border-active-border hover:shadow-elev-lg",
        className,
      )}
      {...rest}
    >
      {children}
    </As>
  );
});

export function CardEyebrow({ children, className }) {
  return (
    <div
      className={cn(
        "text-xs font-semibold uppercase tracking-caps text-eyebrow",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function CardTitle({ children, className, as: As = "h3" }) {
  return (
    <As
      className={cn(
        "mt-3 text-xl font-semibold leading-snug tracking-tight",
        className,
      )}
    >
      {children}
    </As>
  );
}

export function CardBody({ children, className }) {
  return (
    <div className={cn("mt-3 text-sm leading-normal opacity-80", className)}>
      {children}
    </div>
  );
}

export default Card;
